import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { Pagination } from "../../components/crm/Pagination";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { StatusBadge } from "../../components/crm/StatusBadge";
import { Spinner } from "../../components/Spinner";
import { isApiError, listCompanies } from "../../lib/crm-api";
import type { Company, CompanyListParams } from "../../lib/crm-types";
import { formatDate } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface Filters {
  q: string;
  status: "" | "active" | "inactive" | "archived";
  owner: string;
}

const EMPTY_FILTERS: Filters = { q: "", status: "", owner: "" };
const PAGE_LIMIT = 25;

function CompaniesInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canCreate = hasPermission("crm.company.create");

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [ownerInput, setOwnerInput] = useState("");
  const [items, setItems] = useState<Company[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params: CompanyListParams = { limit: PAGE_LIMIT, cursor };
      if (filters.q !== "") params.q = filters.q;
      if (filters.status !== "") params.status = filters.status;
      if (filters.owner !== "") params.owner = filters.owner;
      const page = await listCompanies(params);
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onFilter = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setCursor(undefined);
    setCursorStack([]);
    setFilters({ q: search.trim(), status: filters.status, owner: ownerInput.trim() });
  };

  const onClear = (): void => {
    setSearch("");
    setOwnerInput("");
    setCursor(undefined);
    setCursorStack([]);
    setFilters(EMPTY_FILTERS);
  };

  const locale = lang === "es" ? "es-ES" : "en-US";
  const hasPrev = cursorStack.length > 0;

  return (
    <section className="page" aria-labelledby="crm-companies-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="crm-companies-title">{t("crm.companies.title")}</h1>
          <p className="muted">{t("crm.companies.subtitle")}</p>
        </div>
        {canCreate ? (
          <Link className="btn btn--primary" to="/crm/companies/new">
            {t("crm.companies.new")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="filters" onSubmit={onFilter}>
        <label className="field" htmlFor="companies-q">
          <span>{t("crm.common.search")}</span>
          <input
            id="companies-q"
            value={search}
            placeholder={t("crm.companies.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="field" htmlFor="companies-status">
          <span>{t("crm.common.status")}</span>
          <select
            id="companies-status"
            value={filters.status}
            onChange={(event) => {
              setCursor(undefined);
              setCursorStack([]);
              setFilters((current) => ({
                ...current,
                status: event.target.value as Filters["status"],
              }));
            }}
          >
            <option value="">{t("crm.common.all")}</option>
            <option value="active">{t("crm.status.active")}</option>
            <option value="inactive">{t("crm.status.inactive")}</option>
            <option value="archived">{t("crm.status.archived")}</option>
          </select>
        </label>
        <label className="field" htmlFor="companies-owner">
          <span>{t("crm.common.owner")}</span>
          <input
            id="companies-owner"
            value={ownerInput}
            aria-describedby="companies-owner-hint"
            onChange={(event) => setOwnerInput(event.target.value)}
          />
          <small id="companies-owner-hint" className="field__hint">
            {t("crm.common.ownerHint")}
          </small>
        </label>
        <div className="filters__actions">
          <button type="submit" className="btn btn--primary">
            {t("crm.common.apply")}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClear}>
            {t("crm.common.clear")}
          </button>
        </div>
      </form>

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("crm.companies.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("crm.companies.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("crm.company.name")}</th>
                <th scope="col">{t("crm.company.email")}</th>
                <th scope="col">{t("crm.common.status")}</th>
                <th scope="col">{t("crm.common.updated")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("crm.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((company) => (
                <tr key={company.id}>
                  <td>
                    <Link to={`/crm/companies/${company.id}`}>{company.name}</Link>
                    {company.tax_id !== null ? (
                      <span className="muted"> · {company.tax_id}</span>
                    ) : null}
                  </td>
                  <td>{company.email ?? "—"}</td>
                  <td>
                    <StatusBadge status={company.status} />
                  </td>
                  <td>{formatDate(company.updated_at, locale)}</td>
                  <td className="table__actions-col">
                    <Link className="btn btn--ghost btn--sm" to={`/crm/companies/${company.id}`}>
                      {t("crm.common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Pagination
        hasPrev={hasPrev}
        hasNext={nextCursor !== null}
        busy={loading}
        onPrev={() => {
          if (cursorStack.length === 0) {
            return;
          }
          const previous = cursorStack[cursorStack.length - 1];
          setCursorStack((stack) => stack.slice(0, -1));
          setCursor(previous);
        }}
        onNext={() => {
          if (nextCursor === null) {
            return;
          }
          setCursorStack((stack) => [...stack, cursor]);
          setCursor(nextCursor);
        }}
      />
    </section>
  );
}

export function CompaniesPage(): ReactNode {
  return (
    <RequirePermission permission="crm.company.read">
      <CompaniesInner />
    </RequirePermission>
  );
}
